import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import Cookie from 'js-cookie';
import { isNil, omitBy } from 'lodash';
import { fetchData, getUpperDomain } from '../../utils';
import useError from '../useError';
import useSmartlook from '../useSmartlook';

type Props = {
  children: ReactNode;
};

export type Affiliate = {
  redirectUrl?: string;
  token: string;
  provider: string;
  partner?: string;
  tier?: string;
  channel?: string;
};

type AffiliateForm = {
  [index: string]: string | number;
};

type FetchAffiliateParams = {
  provider?: string;
  queryString?: string;
};

type AffiliateContextType = {
  affiliate?: Affiliate;
  fetchAffiliate: (params: FetchAffiliateParams) => Promise<Affiliate | void>;
  handleAffiliateForm: (token: string) => Promise<AffiliateForm>;
  affiliateForm?: AffiliateForm;
  removeAffiliateCookieData: () => void;
  removeAffiliateData: () => void;
  getAffiliateFormFromSessionStorage: () => AffiliateForm | undefined;
  removeAffiliateFormFromSessionStorage: () => void;
  fetchAffiliateForm: (token: string) => Promise<AffiliateForm>;
};

const AffiliateContext = createContext<AffiliateContextType>(
  {} as AffiliateContextType,
);

const useAffiliate = (): AffiliateContextType => useContext(AffiliateContext);

function getAffiliateFormFromSessionStorage() {
  const data = sessionStorage.getItem('affiliateForm');
  if (data) {
    const form = JSON.parse(data);
    return form;
  }

  return undefined;
}

export function AffiliateProvider({ children }: Props): React.JSX.Element {
  const [affiliate, setAffiliate] = useState<Affiliate>();
  const [affiliateForm, setAffiliateForm] = useState<AffiliateForm | undefined>(
    getAffiliateFormFromSessionStorage(),
  );
  const { showError } = useError();
  const { error } = useSmartlook();

  useEffect(() => {
    const provider = Cookie.get('affiliate_provider');
    const partner = Cookie.get('affiliate_partner') || '';
    const token = Cookie.get('affiliate_token') || '';
    const tier = Cookie.get('affiliate_tier');
    const channel = Cookie.get('affiliate_channel');

    if (!provider) return;

    const storedAffiliate: Affiliate = {
      token,
      provider,
      partner,
      tier,
      channel,
    };

    setAffiliate(storedAffiliate);
  }, []);

  /**
   * Fetches affiliate data based on passed query and url parameters.
   * Sets affiliate data in cookie for upper public domain so it's visible on other products
   */
  const fetchAffiliate = useCallback(
    async ({
      provider,
      queryString = '',
    }: FetchAffiliateParams): Promise<Affiliate | void> => {
      try {
        const url = `/affiliate/data/${provider}${queryString}`;

        const fetchedAffiliate: Affiliate = await fetchData(url.toString());
        setAffiliate(fetchedAffiliate);
        sessionStorage.setItem('affiliate', JSON.stringify(fetchedAffiliate));

        const expires = new Date();
        expires.setDate(expires.getDate() + 30);

        const domain = getUpperDomain();

        const cookieOptions = {
          expires,
          domain,
        };

        Cookie.set('rpts', Date.now().toString());
        Cookie.set(
          'affiliate_token',
          fetchedAffiliate.token || '',
          cookieOptions,
        );
        Cookie.set(
          'affiliate_provider',
          fetchedAffiliate.provider || '',
          cookieOptions,
        );
        Cookie.set(
          'vvs_utmsrc',
          fetchedAffiliate.provider || '',
          cookieOptions,
        );
        Cookie.set(
          'affiliate_partner',
          fetchedAffiliate.partner || '',
          cookieOptions,
        );
        Cookie.set('vvs_utmcpg', fetchedAffiliate.partner || '', cookieOptions);
        Cookie.set('vvs_utmmed', 'affiliate', cookieOptions);

        if (fetchedAffiliate.tier) {
          Cookie.set('affiliate_tier', fetchedAffiliate.tier, cookieOptions);
        }

        if (fetchedAffiliate.channel) {
          Cookie.set(
            'affiliate_channel',
            fetchedAffiliate.channel,
            cookieOptions,
          );
        }

        return fetchedAffiliate;
      } catch (e) {
        error('[AffiliateProvider][fetchAffiliate]', e);
        // Don't show error, because it's first encounter of the client with application,
        // which will start will error, that he has no relation to
        // showError();
        throw e;
      }
    },
    [error, showError],
  );

  const getSanitizedAffiliateForm = useCallback(async (token: string) => {
    const form = await fetchData(`/lead-data?token=${token}`);

    // Removes null values from payload
    const sanitizedForm = omitBy(form, isNil);

    return sanitizedForm;
  }, []);

  /** Fetches the prefilled application form data for registration from the affiliate */
  const handleAffiliateForm = useCallback(
    async (token: string) => {
      try {
        const form = await getSanitizedAffiliateForm(token);
        setAffiliateForm(form);
        sessionStorage.setItem('affiliateForm', JSON.stringify(form));

        return form;
      } catch (e) {
        error('[AffiliateProvider][fetchAffiliateForm]', e);
        showError();
        throw e;
      }
    },
    [error, showError],
  );

  /** Fetches the prefilled application form data for registration from the affiliate */
  const fetchAffiliateForm = useCallback(
    async (token: string) => {
      try {
        const form = await fetchData(`/lead-data?token=${token}`);

        // Removes null values from payload
        const cleanedForm = Object.fromEntries(
          Object.entries(form).filter(([, v]) => v !== null),
        ) as AffiliateForm;

        setAffiliateForm(cleanedForm);

        return cleanedForm;
      } catch (e) {
        error('[AffiliateProvider][fetchAffiliateForm]', e);
        showError();
        throw e;
      }
    },
    [error, showError],
  );

  const removeAffiliateFormFromSessionStorage = useCallback(() => {
    sessionStorage.removeItem('affiliateForm');
  }, []);

  const removeAffiliateCookieData = useCallback(() => {
    try {
      const domain = getUpperDomain();

      const options = {
        domain,
      };

      Cookie.remove('affiliate_provider', options);
      Cookie.remove('affiliate_partner', options);
      Cookie.remove('affiliate_token', options);
      Cookie.remove('affiliate_tier', options);
      Cookie.remove('affiliate_channel', options);

      setAffiliate(undefined);
    } catch (e) {
      error('[AffiliateProvider][removeAffiliateCookieData]', e);
      throw e;
    }
  }, [error]);

  /** Remove the affiliate related cookies and state */
  const removeAffiliateData = useCallback(() => {
    try {
      removeAffiliateCookieData();
      removeAffiliateFormFromSessionStorage();
      setAffiliateForm(undefined);
    } catch (e) {
      showError();
      throw e;
    }
  }, [removeAffiliateCookieData, showError]);

  const affiliateContextValue = useMemo(
    () => ({
      affiliate,
      fetchAffiliate,
      fetchAffiliateForm,
      affiliateForm,
      removeAffiliateCookieData,
      removeAffiliateData,
      handleAffiliateForm,
      getAffiliateFormFromSessionStorage,
      removeAffiliateFormFromSessionStorage,
    }),
    [
      affiliate,
      fetchAffiliate,
      handleAffiliateForm,
      affiliateForm,
      removeAffiliateCookieData,
      removeAffiliateData,
      getAffiliateFormFromSessionStorage,
      removeAffiliateFormFromSessionStorage,
    ],
  );
  return (
    <AffiliateContext.Provider value={affiliateContextValue}>
      {children}
    </AffiliateContext.Provider>
  );
}

export default useAffiliate;
